import { useStore } from '@nanostores/react';
import { workbenchStore, type PanelTab } from '~/lib/stores/workbench';
import { streamingState } from '~/lib/stores/streaming';
import { classNames } from '~/utils/classNames';

export function PanelTabBar() {
  const tabs = useStore(workbenchStore.panelTabs);
  const activeTabId = useStore(workbenchStore.activeTabId);
  // Hide Publish while a build is streaming (initial generation or resume) — you
  // can't ship a half-generated site, so the CTA only returns once the turn ends.
  const isBuilding = useStore(streamingState);

  // Publish (deployment) is the primary action — pinned to the right, set apart from the
  // view/management tabs (which scroll independently on the left), echoing the design's
  // right-aligned Deploy. Splitting them keeps Publish from ever being clipped.
  const deploymentTab =
    tabs.find((t) => t.tool === 'deployment' && !t.closable) ?? tabs.find((t) => t.tool === 'deployment');
  const publishTab = isBuilding ? undefined : deploymentTab;
  // Always keep the deployment tab out of the left (scrolling) group — it lives on
  // the right, or nowhere while building. Never let it fall back into the left list.
  const otherTabs = deploymentTab ? tabs.filter((t) => t !== deploymentTab) : tabs;

  const renderTab = (tab: PanelTab) => {
    const isActive = tab.id === activeTabId;
    const isPublish = tab.tool === 'deployment';

    return (
      <button
        key={tab.id}
        onClick={() => {
          workbenchStore.setActiveTab(tab.id);
        }}
        className={classNames(
          'ui-tab !px-2.5 whitespace-nowrap flex-shrink-0 group',
          isActive && 'ui-tab-active',
          isPublish && 'text-primary font-medium',
        )}
      >
        <div
          className={classNames(
            'text-sm',
            tab.type === 'preview' && 'i-ph:globe-simple',
            tab.type === 'launcher' && 'i-ph:plus',
            tab.tool === 'deployment' && 'i-ph:rocket-launch',
            tab.tool === 'forms' && 'i-ph:envelope-simple',
            tab.tool === 'integrations' && 'i-ph:plugs-connected',
            tab.tool === 'payments' && 'i-ph:credit-card',
            tab.tool === 'shop' && 'i-ph:storefront',
            tab.tool === 'funnels' && 'i-ph:funnel',
            tab.tool === 'marketing' && 'i-ph:megaphone',
            tab.tool === 'reviews' && 'i-ph:star',
            tab.tool === 'content' && 'i-ph:database',
            tab.tool === 'automations' && 'i-ph:lightning',
            tab.tool === 'sections' && 'i-ph:stack',
            tab.tool === 'marketplace' && 'i-ph:squares-four',
            tab.tool === 'assets' && 'i-ph:image',
            tab.tool === 'analytics' && 'i-ph:chart-line',
            tab.tool === 'domains' && 'i-ph:globe',
            tab.tool === 'code' && 'i-ph:code',
            tab.tool === 'sub-agent' && 'i-ph:robot',
            (isActive || isPublish) && 'text-accent',
          )}
        />

        {tab.label}

        {tab.closable && (
          <span
            role="button"
            aria-label={`Close ${tab.label}`}
            onClick={(e) => {
              e.stopPropagation();
              workbenchStore.closeTab(tab.id);
            }}
            className="ml-0.5 p-0.5 rounded text-muted hover:text-primary hover:bg-white/[0.06] opacity-0 group-hover:opacity-100 touch:opacity-100 touch:p-1.5 touch:-my-1 transition-opacity"
          >
            <div className="i-ph:x text-[10px]" />
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex items-center w-full">
      {/* View + management tabs — content width, scroll independently if needed (scrollbar hidden) */}
      <div className="flex items-center gap-0.5 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {otherTabs.map(renderTab)}
      </div>

      {/* Primary action, pushed right by auto-margin — the gap is inert margin, not a hoverable element */}
      {publishTab && (
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto pl-2">
          <div className="w-px h-4 bg-hairline" />
          {renderTab(publishTab)}
        </div>
      )}
    </div>
  );
}
